import React, { useState } from 'react';
import { Plus, Trash2, Edit2, FolderOpen, ChevronRight, Users, ListTodo, X, Share2 } from 'lucide-react';
import './MyLists.css';

export function MyListsScreen({ 
  lists, 
  currentUser,
  onSelectList, 
  onCreateList, 
  onUpdateList,
  onDeleteList,
  onShare,
  onLogout 
}) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [listName, setListName] = useState('');

  const handleCreate = () => {
    if (listName.trim()) {
      const newList = onCreateList(listName);
      setListName('');
      setShowCreateModal(false);
      
      // Auto-select the newly created list
      if (newList) {
        onSelectList(newList);
      }
    }
  };

  const handleEdit = () => {
    if (listName.trim() && editingList) {
      onUpdateList(editingList.id, listName);
      setListName('');
      setEditingList(null);
      setShowEditModal(false);
    }
  };

  const startEdit = (list) => {
    setEditingList(list);
    setListName(list.name);
    setShowEditModal(true);
  };

  const handleDelete = (list, e) => {
    e.stopPropagation(); // Prevent selecting the list
    onDeleteList(list.id);
  };

  const getChoreCount = (listId) => {
    // This will be passed from parent
    return 0; // Placeholder
  };

  const getMemberCount = (list) => {
    return list.members.length;
  };

  const isOwner = (list) => {
    return list.ownerId === currentUser?.id;
  };

  // Safety check for null currentUser
  if (!currentUser) {
    return (
      <div className="my-lists-container">
        <div className="my-lists-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-lists-container">
      <header className="my-lists-header">
        <div className="header-user">
          <div className="user-avatar-small" style={{ backgroundColor: currentUser.color }}>
            {currentUser.name.charAt(0).toUpperCase()}
          </div>
          <span>{currentUser.name}</span>
        </div>
        <button className="btn-logout" onClick={onLogout}>
          Logout
        </button>
      </header>

      <div className="my-lists-content">
        <div className="my-lists-title">
          <FolderOpen size={32} />
          <h1>My Lists</h1>
        </div>

        {lists.length === 0 ? (
          <div className="empty-state">
            <ListTodo size={64} />
            <h3>No lists yet!</h3>
            <p>Create your first list to get started</p>
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={20} /> Create List
            </button>
          </div>
        ) : (
          <>
            <div className="lists-grid">
              {lists.map(list => (
                <div 
                  key={list.id} 
                  className="list-card"
                  onClick={() => onSelectList(list)}
                >
                  <div className="list-card-header">
                    <h3>{list.name}</h3>
                    <div className="list-card-actions" onClick={e => e.stopPropagation()}>
                      <button 
                        className="btn-icon" 
                        onClick={() => onShare(list)}
                        title="Share list"
                      >
                        <Share2 size={16} />
                      </button>
                      {isOwner(list) && (
                        <>
                          <button 
                            className="btn-icon" 
                            onClick={() => startEdit(list)}
                            title="Edit list name"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            className="btn-icon-danger" 
                            onClick={(e) => handleDelete(list, e)}
                            title="Delete list"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="list-card-info">
                    <div className="list-info-item">
                      <Users size={14} />
                      <span>{getMemberCount(list)} member{getMemberCount(list) !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="list-info-item">
                      {isOwner(list) ? (
                        <span className="owner-badge">Owner</span>
                      ) : (
                        <span className="member-badge">Member</span>
                      )}
                    </div>
                  </div>

                  {/* Member Avatars */}
                  {list.members && list.members.length > 0 && (
                    <div className="member-avatars">
                      {list.members.slice(0, 4).map(memberId => {
                        const member = list.memberDetails[memberId];
                        return (
                          <div
                            key={memberId}
                            className="member-avatar-small"
                            style={{ backgroundColor: member.color }}
                            title={member.name}
                          >
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                        );
                      })}
                      {list.members.length > 4 && (
                        <div className="member-avatar-overflow">
                          +{list.members.length - 4}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="list-card-footer">
                    <span>Open list</span>
                    <ChevronRight size={18} />
                  </div>
                </div>
              ))}
            </div>

            <button 
              className="btn-create-list" 
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={20} /> Create New List
            </button>
          </>
        )}
      </div>

      {/* Create List Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New List</h3>
              <button className="btn-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="form-group">
              <label>List Name</label>
              <input
                type="text"
                placeholder="e.g., Home, Work, Kids"
                value={listName}
                onChange={e => setListName(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleCreate()}
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreate}>
                <Plus size={20} /> Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit List Modal */}
      {showEditModal && editingList && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit List Name</h3>
              <button className="btn-close" onClick={() => setShowEditModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="form-group">
              <label>List Name</label>
              <input
                type="text"
                placeholder="List name"
                value={listName}
                onChange={e => setListName(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleEdit()}
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
